import { http } from "./http";

// ─── Confirmation de sécurité : défi et vérification (23/10/2026) ──────────────────────────────────
// Mêmes appels pour la fenêtre de sécurité (`FinancialConfirmationDialog`) et pour la confirmation tapée dans la
// fenêtre « Nouvel encaissement » (`EncaissementConfirm.tsx`). Le défi est lié au contenu exact de l'opération
// (méthode, chemin, corps) : le serveur la contrôle (pré-contrôle F23) avant tout mot de passe ou code.

/** Opération protégée : méthode, chemin et corps exacts de la requête qui suivra. */
export interface FinancialTarget {
  method: string;
  path: string;
  payload: Record<string, unknown>;
}

/**
 * Demande un défi (`POST /v1/fiscal-security/challenges`, corps EXACTEMENT { method, path, payload,
 * verificationMethod, reason }) et rend la réponse telle quelle : la fenêtre de sécurité la lit sans la contrôler,
 * comme avant le 23/10/2026.
 */
export async function requestFinancialChallenge(
  target: FinancialTarget,
  verificationMethod: "password" | "email",
  reason: string,
) {
  const { data } = await http.post<{ challengeId: string; expiresIn?: number }>(
    "/v1/fiscal-security/challenges",
    {
      method: target.method,
      path: target.path,
      payload: target.payload,
      verificationMethod,
      reason,
    },
  );
  return data;
}

/**
 * Défi contrôlé : identifiant requis (sinon « Réponse de confirmation inattendue. »), durée de validité en secondes
 * (5 minutes à défaut).
 */
export async function startFinancialChallenge(
  target: FinancialTarget,
  verificationMethod: "password" | "email",
  reason: string,
): Promise<{ challengeId: string; expiresIn: number }> {
  const data: unknown = await requestFinancialChallenge(target, verificationMethod, reason);
  const { challengeId, expiresIn } = (data ?? {}) as { challengeId?: unknown; expiresIn?: unknown };
  if (typeof challengeId !== "string" || !challengeId)
    throw new Error("Réponse de confirmation inattendue.");
  return { challengeId, expiresIn: Number(expiresIn) || 300 };
}

/**
 * Vérifie le mot de passe ou le code (`POST /v1/fiscal-security/verify`) et rend la preuve, valable pour cette
 * opération seulement. Son format n'est pas contrôlé ici (le serveur le fait à la consommation).
 */
export async function verifyFinancialChallenge(
  challengeId: string,
  secret: string,
): Promise<string> {
  const { data } = await http.post<{ authorization: string }>(
    "/v1/fiscal-security/verify",
    { challengeId, secret },
  );
  return data.authorization;
}
